import { useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Store, MessageSquare, Phone, Power, ShieldAlert, Banknote, MapPin, Mail, Calendar, Users,
  Building2, Receipt, FileText, ShoppingBag, AlertTriangle, ShieldCheck, Radar, Megaphone,
  Brain, Map as MapIcon, History, Lock, EyeOff, Flame, Zap, RefreshCw, Download, ExternalLink,
  Snowflake, KeyRound, Crown, Sparkles, TrendingUp, TrendingDown, CheckCircle2, XCircle,
  Activity, Truck, Clock, Award, Target, AlertCircle, Wallet, Send, Bell, GitBranch,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line,
  AreaChart, Area, PieChart, Pie, Cell, RadialBarChart, RadialBar, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar as RadarShape,
} from "recharts";
import { fmtNum } from "@/lib/format";

const CAT_COLORS: Record<string, string> = {
  canteen: "#d97706", bookshop: "#0ea5e9", uniform: "#9333ea",
  bus: "#7B1416", clinic: "#B33A1A", events: "#8B6F47", general: "#64748b",
};
const CAT_EMOJI: Record<string, string> = {
  canteen: "🍱", bookshop: "📚", uniform: "👕", bus: "🚌", clinic: "💊", events: "🎪", general: "🛒",
};
const CAT_LABEL: Record<string, string> = {
  canteen: "Food & Canteen", bookshop: "Books & Stationery", uniform: "Uniform & Apparel",
  bus: "Transport", clinic: "Medical & Wellness", events: "Events", general: "General",
};

const fmt = (n: number) => n >= 1e9 ? `UGX ${(n/1e9).toFixed(2)}B` : n >= 1e6 ? `UGX ${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `UGX ${(n/1e3).toFixed(0)}K` : `UGX ${Math.round(n)}`;
function hash(s: string) { let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return h; }

type Merchant = {
  id: string; name: string; category: string; school_id: string; active: boolean;
  latitude: number | null; longitude: number | null; address: string | null;
  lifecycle_status: string; contact_phone: string | null; contact_email: string | null;
  fee_rate_bps: number; created_at?: string;
};

function deepMetrics(m: Merchant) {
  const h = Math.abs(hash(m.id));
  const revenue = 1_200_000 + (h % 18) * 850_000;
  const orders = 80 + (h % 240);
  const refunds = (h % 7) / 100;
  const risk = (h % 95);
  const trust = Math.max(40, 100 - risk + (h % 10));
  const settlementDue = revenue * (0.02 + (h % 5) / 1000);
  const schoolsServed = 1 + (h % 32);
  const studentsReached = schoolsServed * (200 + (h % 800));
  const parentsReached = Math.round(studentsReached * 0.7);
  const deliveryRel = 70 + (h % 30);
  const parentRating = 3.4 + ((h % 16) / 10);
  const schoolRating = 3.6 + ((h % 14) / 10);
  const tin = `100${(h % 9999).toString().padStart(4, "0")}${(h % 99).toString().padStart(2, "0")}`;
  const license = `TRD-${2024}-${(h % 9999).toString().padStart(5, "0")}`;
  const mrcId = `MRC-${(h % 99999).toString().padStart(5, "0")}`;
  return {
    revenue, orders, refunds, risk, trust, settlementDue, schoolsServed, studentsReached, parentsReached,
    deliveryRel, parentRating, schoolRating, tin, license, mrcId,
    complaintsOpen: h % 5, complaintsResolved: 8 + (h % 20),
    disputesOpen: h % 3, refundAbuseFlag: risk > 70,
    contractActive: true, kycComplete: risk < 80, uraVerified: true,
    branches: 1 + (h % 3),
  };
}

const seriesDays = (seed: number, days = 30, base = 1_000_000) =>
  Array.from({ length: days }, (_, i) => ({
    d: `D${i + 1}`,
    v: Math.round(base * (0.6 + Math.sin((seed + i) * 0.5) * 0.2 + ((seed + i) % 7) / 20)),
  }));

export function MerchantCommandModal({
  merchant, open, onOpenChange, schoolName,
}: {
  merchant: Merchant | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  schoolName?: string;
}) {
  const [tab, setTab] = useState("overview");
  if (!merchant) return null;
  const m = merchant;
  const x = deepMetrics(m);
  const color = CAT_COLORS[m.category] ?? "#666";

  const act = (label: string, fn?: () => void) => () => { toast.success(label); fn?.(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1280px] max-h-[94vh] w-[96vw] overflow-hidden p-0 gap-0">
        {/* ============ HEADER ============ */}
        <div className="relative border-b" style={{ background: `linear-gradient(135deg, ${color}18, transparent 60%)` }}>
          <div className="flex items-start gap-4 p-5">
            <div className="grid h-16 w-16 place-items-center rounded-2xl text-3xl shadow-md" style={{ background: `${color}25`, color }}>
              {CAT_EMOJI[m.category]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-display text-2xl">{m.name}</h2>
                <Badge variant={m.lifecycle_status === "active" ? "default" : "secondary"} className="capitalize">{m.lifecycle_status}</Badge>
                {x.uraVerified && <Badge variant="outline" className="gap-1 border-emerald-500/40 text-emerald-700 dark:text-emerald-400"><ShieldCheck className="h-3 w-3"/>URA</Badge>}
                {x.kycComplete && <Badge variant="outline" className="gap-1 border-sky-500/40 text-sky-700 dark:text-sky-400"><ShieldCheck className="h-3 w-3"/>KYC</Badge>}
                {x.refundAbuseFlag && <Badge variant="destructive" className="gap-1"><Flame className="h-3 w-3"/>Flagged</Badge>}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="font-mono">{x.mrcId}</span>
                <span>· {CAT_LABEL[m.category]}</span>
                {schoolName && <span>· {schoolName}</span>}
                {m.address && <span className="flex items-center gap-1"><MapPin className="h-3 w-3"/>{m.address}</span>}
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3"/>Joined {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                <ScoreChip label="Trust" value={x.trust} hi />
                <ScoreChip label="Risk" value={x.risk} inverted />
                <MiniStat label="Schools" value={fmtNum(x.schoolsServed)} icon={Building2}/>
                <MiniStat label="Students" value={fmtNum(x.studentsReached)} icon={Users}/>
                <MiniStat label="Term revenue" value={fmt(x.revenue)} icon={TrendingUp}/>
                <MiniStat label="Settlement due" value={fmt(x.settlementDue)} icon={Banknote}/>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Button size="sm" variant="outline" onClick={act("Message thread opened")}><MessageSquare className="mr-1.5 h-3.5 w-3.5"/>Message</Button>
              <Button size="sm" variant="outline" onClick={act(`Calling ${m.contact_phone ?? "merchant"}…`)}><Phone className="mr-1.5 h-3.5 w-3.5"/>Call</Button>
              <Button size="sm" variant="outline" onClick={act("Settlement console opened", () => setTab("settlements"))} className="border-emerald-500/40"><Banknote className="mr-1.5 h-3.5 w-3.5"/>Settlement</Button>
              <Button size="sm" variant="outline" onClick={act("Investigation started")} className="border-amber-500/40"><Radar className="mr-1.5 h-3.5 w-3.5"/>Investigate</Button>
              <Button size="sm" variant={m.lifecycle_status === "suspended" ? "default" : "destructive"} onClick={act(m.lifecycle_status === "suspended" ? "Merchant reactivated" : "Merchant suspended")}><Power className="mr-1.5 h-3.5 w-3.5"/>{m.lifecycle_status === "suspended" ? "Reactivate" : "Suspend"}</Button>
            </div>
          </div>
        </div>

        {/* ============ BODY ============ */}
        <div className="grid max-h-[calc(94vh-180px)] grid-cols-[260px_1fr] overflow-hidden">
          {/* Left rail */}
          <div className="border-r bg-muted/30">
            <ScrollArea className="h-[calc(94vh-260px)]">
              <div className="space-y-3 p-4">
                <HealthCard title="Financial" items={[
                  ["Revenue", "Excellent", "ok"],
                  ["Settlements", "On time", "ok"],
                  ["Refunds", `${(x.refunds * 100).toFixed(1)}%`, x.refunds > 0.05 ? "warn" : "ok"],
                  ["Disputes", String(x.disputesOpen), x.disputesOpen > 0 ? "warn" : "ok"],
                ]}/>
                <HealthCard title="Compliance" items={[
                  ["URA TIN", x.tin, "ok"],
                  ["License", "Valid", "ok"],
                  ["KYC", x.kycComplete ? "Complete" : "Pending", x.kycComplete ? "ok" : "warn"],
                  ["Contract", "Active", "ok"],
                ]}/>
                <HealthCard title="Platform" items={[
                  ["Orders / wk", fmtNum(Math.round(x.orders/4)), "ok"],
                  ["Delivery rel.", `${x.deliveryRel}%`, x.deliveryRel < 80 ? "warn" : "ok"],
                  ["Parent ★", x.parentRating.toFixed(1), "ok"],
                  ["School ★", x.schoolRating.toFixed(1), "ok"],
                ]}/>
                <Card className="border-amber-500/30 bg-amber-500/5 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400"><Crown className="h-3.5 w-3.5"/>Super Admin Powers</div>
                  <div className="mt-2 space-y-1">
                    <SAButton icon={KeyRound} label="Impersonate merchant" onClick={act("Impersonation session opened")} />
                    <SAButton icon={Snowflake} label="Freeze funds" onClick={act("All settlements frozen")} />
                    <SAButton icon={Lock} label="Emergency shutdown" onClick={act("Emergency shutdown engaged")} />
                    <SAButton icon={RefreshCw} label="Rebuild ledger" onClick={act("Revenue reconstruction queued")} />
                    <SAButton icon={ShieldAlert} label="Risk lock" onClick={act("Withdrawals locked")} />
                  </div>
                </Card>
              </div>
            </ScrollArea>
          </div>

          {/* Main */}
          <div className="flex flex-col overflow-hidden">
            <Tabs value={tab} onValueChange={setTab} className="flex flex-1 flex-col overflow-hidden">
              <div className="border-b px-3 py-2">
                <TabsList className="flex w-full flex-wrap gap-1 bg-transparent p-0 h-auto">
                  {TABS.map(t => (
                    <TabsTrigger key={t.v} value={t.v} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1 px-2.5 py-1 text-[11px]">
                      <t.i className="h-3 w-3"/>{t.l}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-5">
                  <TabsContent value="overview" className="m-0"><OverviewTab m={m} x={x} schoolName={schoolName}/></TabsContent>
                  <TabsContent value="financials" className="m-0"><FinancialsTab m={m} x={x}/></TabsContent>
                  <TabsContent value="settlements" className="m-0"><SettlementsTab m={m} x={x}/></TabsContent>
                  <TabsContent value="schools" className="m-0"><SchoolsTab x={x}/></TabsContent>
                  <TabsContent value="products" className="m-0"><ProductsTab x={x}/></TabsContent>
                  <TabsContent value="orders" className="m-0"><OrdersTab x={x}/></TabsContent>
                  <TabsContent value="students" className="m-0"><StudentsTab x={x}/></TabsContent>
                  <TabsContent value="complaints" className="m-0"><ComplaintsTab x={x}/></TabsContent>
                  <TabsContent value="compliance" className="m-0"><ComplianceTab m={m} x={x}/></TabsContent>
                  <TabsContent value="risk" className="m-0"><RiskTab x={x}/></TabsContent>
                  <TabsContent value="comms" className="m-0"><CommsTab m={m}/></TabsContent>
                  <TabsContent value="ai" className="m-0"><AITab m={m} x={x}/></TabsContent>
                  <TabsContent value="geo" className="m-0"><GeoTab m={m} x={x}/></TabsContent>
                  <TabsContent value="audit" className="m-0"><AuditTab m={m}/></TabsContent>
                  <TabsContent value="twin" className="m-0"><TwinTab x={x}/></TabsContent>
                </div>
              </ScrollArea>
            </Tabs>

            {/* Floating action bar */}
            <div className="border-t bg-card/95 px-4 py-2 backdrop-blur">
              <div className="flex flex-wrap items-center gap-1.5">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={act(`Released ${fmt(x.settlementDue)} to ${m.name}`)}><Banknote className="mr-1 h-3.5 w-3.5"/>Release settlement</Button>
                <Button size="sm" variant="outline" onClick={act("Settlement held pending review")}><Lock className="mr-1 h-3.5 w-3.5"/>Hold</Button>
                <Button size="sm" variant="outline" onClick={act("Investigation case opened")}><Radar className="mr-1 h-3.5 w-3.5"/>Investigate</Button>
                <Button size="sm" variant="outline" onClick={act("Funds frozen")}><Snowflake className="mr-1 h-3.5 w-3.5"/>Freeze</Button>
                <Button size="sm" variant="outline" onClick={act("Profile export queued")}><Download className="mr-1 h-3.5 w-3.5"/>Export</Button>
                <Button size="sm" variant="outline" onClick={act("Outreach sent")}><Send className="mr-1 h-3.5 w-3.5"/>Contact</Button>
                <div className="ml-auto text-[11px] text-muted-foreground">All actions are logged · Audit ID <span className="font-mono">{x.mrcId}-{Date.now().toString().slice(-5)}</span></div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TABS = [
  { v: "overview", l: "Overview", i: Store },
  { v: "financials", l: "Financials", i: TrendingUp },
  { v: "settlements", l: "Settlements", i: Banknote },
  { v: "schools", l: "Schools", i: Building2 },
  { v: "products", l: "Products", i: ShoppingBag },
  { v: "orders", l: "Orders", i: Receipt },
  { v: "students", l: "Students & Parents", i: Users },
  { v: "complaints", l: "Complaints", i: AlertTriangle },
  { v: "compliance", l: "Compliance", i: ShieldCheck },
  { v: "risk", l: "Risk & Fraud", i: ShieldAlert },
  { v: "comms", l: "Communications", i: Megaphone },
  { v: "ai", l: "AI Intelligence", i: Brain },
  { v: "geo", l: "Geographic", i: MapIcon },
  { v: "audit", l: "Audit Trail", i: History },
  { v: "twin", l: "Digital Twin", i: Sparkles },
];

/* ===== shared bits ===== */
function ScoreChip({ label, value, hi, inverted }: { label: string; value: number; hi?: boolean; inverted?: boolean }) {
  const good = inverted ? value < 40 : value >= 75;
  const warn = inverted ? value < 70 : value >= 50;
  const tone = good ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400" : warn ? "border-amber-500/40 text-amber-700 dark:text-amber-400" : "border-destructive/40 text-destructive";
  return (
    <div className={`rounded-lg border bg-card/60 p-2 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label} score</div>
      <div className="font-display text-xl">{value}<span className="text-xs opacity-60">/100</span></div>
    </div>
  );
}
function MiniStat({ label, value, icon: I }: { label: string; value: string; icon: any }) {
  return (
    <div className="rounded-lg border bg-card/60 p-2">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><I className="h-3 w-3"/>{label}</div>
      <div className="mt-0.5 font-display text-sm">{value}</div>
    </div>
  );
}
function HealthCard({ title, items }: { title: string; items: [string, string, "ok"|"warn"|"danger"][] }) {
  return (
    <Card className="p-3">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{title} health</div>
      <div className="space-y-1.5">
        {items.map(([k, v, t]) => (
          <div key={k} className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{k}</span>
            <span className={`flex items-center gap-1 font-medium ${t === "ok" ? "text-emerald-600" : t === "warn" ? "text-amber-600" : "text-destructive"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${t === "ok" ? "bg-emerald-500" : t === "warn" ? "bg-amber-500" : "bg-destructive"}`}/>{v}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}
function SAButton({ icon: I, label, onClick }: { icon: any; label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex w-full items-center gap-2 rounded-md border border-amber-500/20 bg-card/60 px-2 py-1.5 text-[11px] hover:bg-amber-500/10">
      <I className="h-3 w-3 text-amber-600"/>{label}
    </button>
  );
}
function Section({ title, icon: I, right, children }: { title: string; icon?: any; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-sm">{I && <I className="h-4 w-4 text-muted-foreground"/>}{title}</div>
        {right}
      </div>
      {children}
    </Card>
  );
}
function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return <div className="flex items-center justify-between border-b py-1.5 text-xs last:border-0"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>;
}

/* ===== tabs ===== */
function OverviewTab({ m, x, schoolName }: any) {
  const timeline = [
    { y: "2024", e: "Joined TriSphere", icon: Sparkles },
    { y: "2024", e: `First school onboarded — ${schoolName ?? "anchor school"}`, icon: Building2 },
    { y: "2025", e: "First settlement released", icon: Banknote },
    { y: "2025", e: `Expanded to ${x.schoolsServed} schools`, icon: TrendingUp },
    { y: "2026", e: `${fmtNum(x.studentsReached)} students reached`, icon: Users },
  ];
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Section title="Business information" icon={Building2}>
        <KV k="Legal name" v={m.name + " Ltd"}/>
        <KV k="Trading name" v={m.name}/>
        <KV k="Category" v={CAT_LABEL[m.category]}/>
        <KV k="TIN" v={<span className="font-mono">{x.tin}</span>}/>
        <KV k="License" v={<span className="font-mono">{x.license}</span>}/>
        <KV k="Branches" v={x.branches}/>
      </Section>
      <Section title="Contacts" icon={Phone}>
        <KV k="Owner" v="John Mukasa"/>
        <KV k="Manager" v="Sarah Nansubuga"/>
        <KV k="Finance" v="Daniel Okello"/>
        <KV k="Phone" v={m.contact_phone ?? "—"}/>
        <KV k="Email" v={m.contact_email ?? "—"}/>
        <KV k="Address" v={m.address ?? "—"}/>
      </Section>
      <Section title="Reach summary" icon={Users}>
        <KV k="Schools served" v={fmtNum(x.schoolsServed)}/>
        <KV k="Students reached" v={fmtNum(x.studentsReached)}/>
        <KV k="Parents reached" v={fmtNum(x.parentsReached)}/>
        <KV k="Parent rating" v={`★ ${x.parentRating.toFixed(1)}`}/>
        <KV k="School rating" v={`★ ${x.schoolRating.toFixed(1)}`}/>
        <KV k="Influence rank" v={`Top ${(100 - x.trust + 5)}%`}/>
      </Section>
      <Card className="p-4 lg:col-span-3">
        <div className="mb-3 font-display text-sm">Relationship timeline</div>
        <div className="relative ml-3 space-y-3 border-l pl-5">
          {timeline.map((t, i) => (
            <div key={i} className="relative">
              <span className="absolute -left-[27px] grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground"><t.icon className="h-3 w-3"/></span>
              <div className="text-xs font-mono text-muted-foreground">{t.y}</div>
              <div className="text-sm">{t.e}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function FinancialsTab({ m, x }: any) {
  const data = useMemo(() => seriesDays(Math.abs(hash(m.id)), 30, x.revenue / 30), [m.id, x.revenue]);
  const byCat = [
    { name: "Snacks", v: x.revenue * 0.42 },
    { name: "Meals", v: x.revenue * 0.31 },
    { name: "Drinks", v: x.revenue * 0.18 },
    { name: "Other", v: x.revenue * 0.09 },
  ];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-5">
        <KpiTile label="Today" value={fmt(x.revenue / 60)} trend="+8%"/>
        <KpiTile label="This week" value={fmt(x.revenue / 12)} trend="+12%"/>
        <KpiTile label="This month" value={fmt(x.revenue / 3)} trend="+4%"/>
        <KpiTile label="Term" value={fmt(x.revenue)} trend="+22%"/>
        <KpiTile label="Year est." value={fmt(x.revenue * 3)} trend="+18%"/>
      </div>
      <Section title="Revenue trend · last 30 days" icon={TrendingUp}>
        <div className="h-60"><ResponsiveContainer><AreaChart data={data}>
          <defs><linearGradient id="rev" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#7B1416" stopOpacity={0.5}/><stop offset="100%" stopColor="#7B1416" stopOpacity={0}/></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2}/><XAxis dataKey="d" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={(v)=>`${(v/1e3).toFixed(0)}K`}/>
          <Tooltip formatter={(v:any)=>fmt(v as number)}/>
          <Area type="monotone" dataKey="v" stroke="#7B1416" fill="url(#rev)" strokeWidth={2}/>
        </AreaChart></ResponsiveContainer></div>
      </Section>
      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Revenue by product category" icon={ShoppingBag}>
          <div className="h-52"><ResponsiveContainer><BarChart data={byCat}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2}/><XAxis dataKey="name" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={(v)=>`${(v/1e6).toFixed(0)}M`}/>
            <Tooltip formatter={(v:any)=>fmt(v as number)}/><Bar dataKey="v" fill="#d97706" radius={[4,4,0,0]}/>
          </BarChart></ResponsiveContainer></div>
        </Section>
        <Section title="Merchant ledger" icon={Receipt} right={<Button size="sm" variant="outline"><Download className="mr-1 h-3 w-3"/>Export</Button>}>
          <div className="text-xs">
            <div className="grid grid-cols-[1fr_2fr_1fr_1fr_1fr] gap-2 border-b pb-1 font-medium text-muted-foreground">
              <span>Date</span><span>Description</span><span>Credit</span><span>Debit</span><span>Balance</span>
            </div>
            {Array.from({length:6}).map((_,i)=>{
              const bal = x.revenue - i*120000;
              return (
                <div key={i} className="grid grid-cols-[1fr_2fr_1fr_1fr_1fr] gap-2 border-b py-1.5">
                  <span className="font-mono">D-{i+1}</span>
                  <span>{["POS sales batch","Wallet top-up","Refund issued","Settlement","Fee","POS sales batch"][i]}</span>
                  <span className="text-emerald-600">{i%3===0?fmt(220000):"—"}</span>
                  <span className="text-destructive">{i%3!==0?fmt(80000):"—"}</span>
                  <span className="font-mono">{fmt(bal)}</span>
                </div>
              );
            })}
          </div>
        </Section>
      </div>
    </div>
  );
}

function KpiTile({ label, value, trend }: { label: string; value: string; trend: string }) {
  const up = trend.startsWith("+");
  return (
    <Card className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-lg">{value}</div>
      <div className={`text-[11px] ${up?"text-emerald-600":"text-destructive"}`}>{up?<TrendingUp className="mr-0.5 inline h-3 w-3"/>:<TrendingDown className="mr-0.5 inline h-3 w-3"/>}{trend}</div>
    </Card>
  );
}

function SettlementsTab({ m, x }: any) {
  const queue = [
    { id: "ST-1029", period: "This week", amount: x.settlementDue, status: "pending", school: "Multiple" },
    { id: "ST-1028", period: "Last week", amount: x.settlementDue * 0.92, status: "completed", school: "Multiple" },
    { id: "ST-1027", period: "2 weeks ago", amount: x.settlementDue * 1.05, status: "completed", school: "Multiple" },
    { id: "ST-1026", period: "3 weeks ago", amount: x.settlementDue * 0.18, status: "held", school: "Refund dispute" },
  ];
  const tone: Record<string,string> = { pending: "bg-amber-500/15 text-amber-700", completed: "bg-emerald-500/15 text-emerald-700", held: "bg-destructive/15 text-destructive", processing: "bg-sky-500/15 text-sky-700", failed: "bg-destructive/15 text-destructive" };
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-6">
        <KpiTile label="Pending" value={fmt(x.settlementDue)} trend="+0%"/>
        <KpiTile label="Processing" value={fmt(0)} trend="+0%"/>
        <KpiTile label="Completed (M)" value={fmt(x.settlementDue * 3.6)} trend="+8%"/>
        <KpiTile label="Held" value={fmt(x.settlementDue * 0.18)} trend="-2%"/>
        <KpiTile label="Failed" value={fmt(0)} trend="+0%"/>
        <KpiTile label="Avg cycle" value="T+1.2d" trend="+0%"/>
      </div>
      <Section title="Settlement queue" icon={Banknote}>
        <div className="space-y-1.5 text-xs">
          {queue.map(s => (
            <div key={s.id} className="grid grid-cols-[1fr_1.5fr_2fr_1.5fr_2fr] items-center gap-2 rounded border p-2">
              <span className="font-mono">{s.id}</span>
              <span>{s.period}</span>
              <span className="font-medium">{fmt(s.amount)}</span>
              <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${tone[s.status]}`}>{s.status}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => toast.success(`Released ${s.id}`)}>Release</Button>
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => toast.success(`Held ${s.id}`)}>Hold</Button>
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => toast.success(`Reversed ${s.id}`)}>Reverse</Button>
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => toast.success(`Recalculated ${s.id}`)}>Recalc</Button>
              </div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Settlement forecast" icon={Sparkles}>
        <div className="grid gap-3 md:grid-cols-3">
          <ForecastCard period="Next week" value={fmt(x.settlementDue * 1.08)} conf={92}/>
          <ForecastCard period="Next month" value={fmt(x.settlementDue * 4.4)} conf={87}/>
          <ForecastCard period="Next term" value={fmt(x.settlementDue * 14)} conf={78}/>
        </div>
      </Section>
    </div>
  );
}
function ForecastCard({ period, value, conf }: { period: string; value: string; conf: number }) {
  return (
    <Card className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{period}</div>
      <div className="mt-1 font-display text-lg">{value}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">Confidence {conf}%</div>
      <Progress value={conf} className="mt-1 h-1"/>
    </Card>
  );
}

function SchoolsTab({ x }: any) {
  const schools = Array.from({ length: Math.min(8, x.schoolsServed) }).map((_, i) => ({
    name: ["Greenhill Academy","Kampala Parents'","Aga Khan","Kabojja Junior","Buddo Primary","St. Mary's","Vienna College","Hana Mixed"][i % 8],
    revenue: x.revenue / (i + 1) / 2,
    students: Math.round(x.studentsReached / (i + 1) / 4),
    parents: Math.round(x.parentsReached / (i + 1) / 4),
  }));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">{x.schoolsServed} schools served · multi-school agreements active</div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => toast.success("Add school flow opened")}>Add school</Button>
          <Button size="sm" variant="outline" onClick={() => toast.success("Exclusive agreement drafted")}>Exclusive agreement</Button>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {schools.map(s => (
          <Card key={s.name} className="p-3">
            <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground"/><span className="font-medium text-sm">{s.name}</span></div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div><div className="text-muted-foreground text-[10px]">Revenue</div><div className="font-mono">{fmt(s.revenue)}</div></div>
              <div><div className="text-muted-foreground text-[10px]">Students</div><div className="font-mono">{fmtNum(s.students)}</div></div>
              <div><div className="text-muted-foreground text-[10px]">Parents</div><div className="font-mono">{fmtNum(s.parents)}</div></div>
              <div><div className="text-muted-foreground text-[10px]">Activity</div><div className="font-mono text-emerald-600">High</div></div>
            </div>
            <div className="mt-2 flex gap-1">
              <Button size="sm" variant="outline" className="h-6 flex-1 px-2 text-[10px]">Restrict</Button>
              <Button size="sm" variant="outline" className="h-6 flex-1 px-2 text-[10px]">Remove</Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ProductsTab({ x }: any) {
  const products = [
    { name: "Chapati & beans", price: 3500, sold: 2840, status: "approved" },
    { name: "Pilau special", price: 6000, sold: 1920, status: "approved" },
    { name: "Soda 500ml", price: 2000, sold: 4500, status: "approved" },
    { name: "Snickers (junk)", price: 4500, sold: 120, status: "restricted" },
    { name: "Fresh juice", price: 3000, sold: 1820, status: "approved" },
    { name: "Energy drink", price: 5000, sold: 0, status: "rejected" },
  ];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{products.length} active SKUs · price changes require approval</div>
        <Button size="sm" variant="outline" onClick={() => toast.success("New product approval queue opened")}>Approve all pending</Button>
      </div>
      <div className="grid gap-2">
        {products.map(p => (
          <div key={p.name} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-2 rounded border p-2 text-sm">
            <span>{p.name}</span>
            <span className="font-mono text-xs">{fmt(p.price)}</span>
            <span className="text-xs text-muted-foreground">{fmtNum(p.sold)} sold</span>
            <Badge variant={p.status === "approved" ? "default" : p.status === "rejected" ? "destructive" : "secondary"} className="w-fit text-[10px]">{p.status}</Badge>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => toast.success("Approved")}>Approve</Button>
              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => toast.success("Restricted")}>Restrict</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab({ x }: any) {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-4">
        <KpiTile label="Today" value={fmtNum(Math.round(x.orders/30))} trend="+12%"/>
        <KpiTile label="This week" value={fmtNum(Math.round(x.orders/4))} trend="+6%"/>
        <KpiTile label="Avg basket" value={fmt(x.revenue/x.orders/30)} trend="+3%"/>
        <KpiTile label="Refunds" value={`${(x.refunds*100).toFixed(2)}%`} trend="-1%"/>
      </div>
      <Section title="Recent orders" icon={Receipt}>
        <div className="space-y-1 text-xs">
          {Array.from({length: 8}).map((_,i) => (
            <div key={i} className="grid grid-cols-[1fr_1.5fr_1.5fr_1fr_1fr_auto] items-center gap-2 rounded border p-2">
              <span className="font-mono">#OR-{(10000+i).toString()}</span>
              <span>Student {i%3===0?"Trevor M.":i%3===1?"Amina K.":"Brian O."}</span>
              <span>{["Pilau special","Chapati & beans","Soda 500ml","Fresh juice"][i%4]}</span>
              <span className="font-mono">{fmt(3000+i*500)}</span>
              <Badge variant="outline" className="w-fit text-[10px]">{i%4===0?"settled":i%4===1?"wallet":i%4===2?"refunded":"settled"}</Badge>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"><ExternalLink className="h-3 w-3"/></Button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function StudentsTab({ x }: any) {
  const data = [
    { age: "P1-P3", v: x.studentsReached*0.2 },
    { age: "P4-P7", v: x.studentsReached*0.35 },
    { age: "S1-S4", v: x.studentsReached*0.3 },
    { age: "S5-S6", v: x.studentsReached*0.15 },
  ];
  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-3">
        <KpiTile label="Students" value={fmtNum(x.studentsReached)} trend="+18%"/>
        <KpiTile label="Parents" value={fmtNum(x.parentsReached)} trend="+12%"/>
        <KpiTile label="Active buyers" value={fmtNum(Math.round(x.studentsReached*0.6))} trend="+9%"/>
      </div>
      <Section title="Demographics by age band" icon={Users}>
        <div className="h-52"><ResponsiveContainer><BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2}/><XAxis dataKey="age" tick={{fontSize:11}}/><YAxis tick={{fontSize:10}}/>
          <Tooltip formatter={(v:any)=>fmtNum(v as number)}/><Bar dataKey="v" fill="#0ea5e9" radius={[4,4,0,0]}/>
        </BarChart></ResponsiveContainer></div>
      </Section>
      <Section title="Spending patterns" icon={Activity}>
        <div className="grid gap-3 md:grid-cols-3 text-sm">
          <KV k="Peak day" v="Wednesday"/>
          <KV k="Peak hour" v="12:30 — 13:15"/>
          <KV k="Avg spend / wk" v={fmt(8500)}/>
          <KV k="Repeat rate" v="68%"/>
          <KV k="Wallet share" v="74%"/>
          <KV k="Cash share" v="26%"/>
        </div>
      </Section>
    </div>
  );
}

function ComplaintsTab({ x }: any) {
  const items = [
    { id: "C-2310", reason: "Wrong item", status: "open", parent: "Mrs Okello", days: 2 },
    { id: "C-2308", reason: "Overcharge", status: "pending", parent: "Mr Walusimbi", days: 5 },
    { id: "C-2305", reason: "Quality issue", status: "resolved", parent: "Mrs Asiimwe", days: 12 },
    { id: "C-2300", reason: "Missing item", status: "resolved", parent: "Mr Lukwago", days: 18 },
  ];
  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-4">
        <KpiTile label="Open" value={String(x.complaintsOpen)} trend="-1%"/>
        <KpiTile label="Pending" value="2" trend="+0%"/>
        <KpiTile label="Resolved (M)" value={String(x.complaintsResolved)} trend="+15%"/>
        <KpiTile label="Avg resolution" value="2.1 days" trend="-12%"/>
      </div>
      <Section title="Cases" icon={AlertTriangle}>
        <div className="space-y-1 text-xs">
          {items.map(c => (
            <div key={c.id} className="grid grid-cols-[1fr_1.5fr_2fr_1fr_1fr_auto] items-center gap-2 rounded border p-2">
              <span className="font-mono">{c.id}</span>
              <span>{c.reason}</span>
              <span>{c.parent}</span>
              <span className="text-muted-foreground">{c.days}d</span>
              <Badge variant={c.status==="resolved"?"default":c.status==="open"?"destructive":"secondary"} className="w-fit text-[10px]">{c.status}</Badge>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={()=>toast.success("Assigned")}>Assign</Button>
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={()=>toast.success("Refunded")}>Refund</Button>
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={()=>toast.success("Escalated")}>Escalate</Button>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function ComplianceTab({ m, x }: any) {
  const docs = [
    { name: "Trading license", number: x.license, status: "valid", expires: "2026-12-31" },
    { name: "URA TIN", number: x.tin, status: "valid", expires: "—" },
    { name: "KYC owner ID", number: "NIN CM900...", status: x.kycComplete?"valid":"pending", expires: "—" },
    { name: "Food handlers cert.", number: "FH-2025-0083", status: "valid", expires: "2026-04-20" },
    { name: "Insurance", number: "INS-UAP-77231", status: "expiring", expires: "2026-08-10" },
    { name: "Contract", number: "SCH-OS-MOU-114", status: "valid", expires: "2027-01-01" },
  ];
  return (
    <div className="space-y-3">
      <div className="grid gap-2">
        {docs.map(d => (
          <div key={d.name} className="grid grid-cols-[2fr_2fr_1fr_1.5fr_auto] items-center gap-2 rounded border p-2 text-xs">
            <span className="font-medium">{d.name}</span>
            <span className="font-mono">{d.number}</span>
            <Badge variant={d.status==="valid"?"default":d.status==="expiring"?"secondary":"destructive"} className="w-fit text-[10px]">{d.status}</Badge>
            <span className="text-muted-foreground">Expires {d.expires}</span>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={()=>toast.success("Document reviewed")}>Review</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RiskTab({ x }: any) {
  const flags = [
    { ok: x.refunds < 0.05, txt: "Refund rate within tolerance" },
    { ok: x.risk < 50, txt: "No circular transaction pattern detected" },
    { ok: x.risk < 60, txt: "Wallet usage normal" },
    { ok: x.risk < 70, txt: "No fake purchase clusters" },
    { ok: x.risk < 80, txt: "Settlement velocity normal" },
  ];
  const radar = [
    { k: "Refund", v: 100 - x.refunds*1000 },
    { k: "Velocity", v: 100 - x.risk*0.6 },
    { k: "Reputation", v: x.parentRating*20 },
    { k: "Compliance", v: x.kycComplete?92:55 },
    { k: "Network", v: 100 - x.risk*0.4 },
    { k: "Identity", v: x.uraVerified?96:50 },
  ];
  return (
    <div className="grid gap-3 lg:grid-cols-3">
      <Card className="p-4 lg:col-span-1">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Composite risk score</div>
        <div className="mt-1 flex items-end gap-2">
          <div className={`font-display text-4xl ${x.risk<40?"text-emerald-600":x.risk<70?"text-amber-600":"text-destructive"}`}>{x.risk}</div>
          <div className="pb-2 text-xs text-muted-foreground">/ 100</div>
        </div>
        <Progress value={x.risk} className="mt-3 h-2"/>
        <div className="mt-4 space-y-1.5 text-xs">
          {flags.map((f,i)=>(
            <div key={i} className="flex items-center gap-2">
              {f.ok?<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600"/>:<XCircle className="h-3.5 w-3.5 text-destructive"/>}
              <span className={f.ok?"":"text-destructive"}>{f.txt}</span>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-4 lg:col-span-2">
        <div className="mb-2 font-display text-sm">Risk surface</div>
        <div className="h-60"><ResponsiveContainer><RadarChart data={radar}>
          <PolarGrid/><PolarAngleAxis dataKey="k" tick={{fontSize:11}}/><PolarRadiusAxis tick={{fontSize:9}}/>
          <RadarShape dataKey="v" stroke="#B33A1A" fill="#B33A1A" fillOpacity={0.3}/>
        </RadarChart></ResponsiveContainer></div>
      </Card>
      <Card className="p-4 lg:col-span-3">
        <div className="mb-3 flex items-center justify-between">
          <div className="font-display text-sm">Investigation center</div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={()=>toast.success("Investigation opened")}><Radar className="mr-1 h-3.5 w-3.5"/>Launch</Button>
            <Button size="sm" variant="outline" onClick={()=>toast.success("Officer assigned")}>Assign officer</Button>
            <Button size="sm" variant="destructive" onClick={()=>toast.success("Settlements frozen")}>Freeze settlements</Button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">Trigger a deep-dive case combining ledger reconstruction, settlement holds, transaction graph analysis and parent/school surveys.</div>
      </Card>
    </div>
  );
}

function CommsTab({ m }: any) {
  const [msg, setMsg] = useState("");
  const [channel, setChannel] = useState("sms");
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Section title="Send direct message" icon={MessageSquare}>
        <div className="mb-2 flex gap-1">
          {["sms","email","whatsapp","platform"].map(c=>(
            <Button key={c} size="sm" variant={channel===c?"default":"outline"} className="capitalize text-xs" onClick={()=>setChannel(c)}>{c}</Button>
          ))}
        </div>
        <Textarea placeholder={`Compose ${channel} to ${m.contact_phone ?? m.contact_email ?? m.name}…`} value={msg} onChange={(e)=>setMsg(e.target.value)} className="min-h-[120px]"/>
        <Button className="mt-2 w-full" onClick={()=>{toast.success(`${channel.toUpperCase()} sent`); setMsg("");}}><Send className="mr-1.5 h-3.5 w-3.5"/>Send</Button>
      </Section>
      <Section title="Broadcast templates" icon={Megaphone}>
        <div className="space-y-2">
          {[
            ["Settlement scheduled", "Notify of next payout window"],
            ["Compliance reminder", "Document expiring in 30 days"],
            ["Performance praise", "Top performer this term"],
            ["Policy update", "Pricing & restricted items update"],
            ["Urgent compliance hold", "Settlement held pending docs"],
          ].map(([t,sub])=>(
            <button key={t} onClick={()=>toast.success(`Sent: ${t}`)} className="flex w-full items-center justify-between rounded border p-2 text-left text-xs hover:bg-accent">
              <div><div className="font-medium">{t}</div><div className="text-muted-foreground text-[11px]">{sub}</div></div>
              <Bell className="h-3.5 w-3.5 text-muted-foreground"/>
            </button>
          ))}
        </div>
      </Section>
    </div>
  );
}

function AITab({ m, x }: any) {
  const [q, setQ] = useState("");
  const suggestions = [
    "Why did revenue change this month?",
    "Which schools generate most sales?",
    "Is this merchant risky?",
    "Should this merchant expand?",
    "What products should they add?",
    "How does this merchant compare to peers?",
  ];
  const [answer, setAnswer] = useState<string | null>(null);
  const ask = (text: string) => {
    setQ(text);
    setAnswer(
      `${m.name} is performing in the ${x.trust>80?"top quartile":"upper half"} of ${CAT_LABEL[m.category]} merchants. ` +
      `Revenue trend is +${(Math.abs(hash(m.id))%30)+5}% MoM driven by ${x.schoolsServed} active schools. ` +
      `Risk score ${x.risk}/100 with ${x.refunds*100<3?"low":"moderate"} refund pressure. ` +
      `Recommended next moves: ${x.risk>60?"hold next settlement pending KYC refresh, ":""}expand into ${["Mukono","Wakiso","Mbarara","Jinja"][Math.abs(hash(m.id))%4]} where demand exceeds supply by ~30%.`
    );
  };
  return (
    <div className="space-y-3">
      <Card className="p-4">
        <div className="mb-3 flex items-center gap-2 font-display text-sm"><Brain className="h-4 w-4 text-primary"/>Merchant Copilot</div>
        <div className="flex gap-2">
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Ask anything about this merchant…"/>
          <Button onClick={()=>q && ask(q)}><Sparkles className="mr-1 h-3.5 w-3.5"/>Ask</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1">
          {suggestions.map(s=>(
            <button key={s} onClick={()=>ask(s)} className="rounded-full border bg-card px-2.5 py-1 text-[11px] hover:bg-accent">{s}</button>
          ))}
        </div>
        {answer && (
          <div className="mt-4 rounded border-l-4 border-primary bg-primary/5 p-3 text-sm">{answer}</div>
        )}
      </Card>
    </div>
  );
}

function GeoTab({ m, x }: any) {
  const opps = [
    { area: "Mukono", demand: "High", competition: "Low", potential: x.revenue * 0.6 },
    { area: "Wakiso", demand: "Medium", competition: "Medium", potential: x.revenue * 0.35 },
    { area: "Mbarara", demand: "High", competition: "Low", potential: x.revenue * 0.45 },
    { area: "Jinja", demand: "Medium", competition: "High", potential: x.revenue * 0.18 },
  ];
  return (
    <div className="space-y-3">
      <Card className="overflow-hidden p-0">
        <div className="aspect-[16/7] w-full">
          {m.latitude && m.longitude ? (
            <iframe
              title="catchment"
              src={`https://www.google.com/maps?q=${m.latitude},${m.longitude}&z=11&output=embed`}
              className="h-full w-full border-0"
              loading="lazy"
            />
          ) : (
            <div className="grid h-full w-full place-items-center text-sm text-muted-foreground">No GPS pin recorded</div>
          )}
        </div>
      </Card>
      <Section title="Expansion opportunities" icon={Target}>
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
          {opps.map(o=>(
            <Card key={o.area} className="p-3">
              <div className="font-display text-base">{o.area}</div>
              <div className="mt-1 text-[11px] text-muted-foreground">Demand <span className="font-medium text-foreground">{o.demand}</span> · Competition <span className="font-medium text-foreground">{o.competition}</span></div>
              <div className="mt-2 text-xs">Potential</div>
              <div className="font-display text-lg text-emerald-600">{fmt(o.potential)} / mo</div>
              <Button size="sm" variant="outline" className="mt-2 w-full text-[11px]" onClick={()=>toast.success(`${o.area} expansion plan drafted`)}>Draft plan</Button>
            </Card>
          ))}
        </div>
      </Section>
    </div>
  );
}

function AuditTab({ m }: any) {
  const events = [
    { t: "5m ago", who: "Super Admin", what: "Viewed merchant profile" },
    { t: "2h ago", who: "Settlement Bot", what: `Released settlement ST-1028 (${fmt(820000)})` },
    { t: "1d ago", who: "School Admin · Greenhill", what: "Approved new product 'Pilau special'" },
    { t: "2d ago", who: "Merchant Owner", what: "Updated trading license number" },
    { t: "5d ago", who: "Compliance Officer", what: "KYC review completed" },
    { t: "8d ago", who: "Super Admin", what: "Added school 'Aga Khan'" },
    { t: "14d ago", who: "Risk Engine", what: "Risk score recalculated → 32" },
  ];
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-sm"><History className="h-4 w-4"/>Full audit trail</div>
        <Button size="sm" variant="outline"><Download className="mr-1 h-3 w-3"/>Export</Button>
      </div>
      <div className="relative ml-3 space-y-3 border-l pl-5">
        {events.map((e,i)=>(
          <div key={i} className="relative">
            <span className="absolute -left-[27px] h-3 w-3 rounded-full bg-primary"/>
            <div className="text-[10px] font-mono text-muted-foreground">{e.t} · {e.who}</div>
            <div className="text-sm">{e.what}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TwinTab({ x }: any) {
  const tw = [
    { l: "Revenue today (live)", v: fmt(x.revenue/60), tone: "emerald" },
    { l: "Projected revenue (term)", v: fmt(x.revenue*1.12), tone: "emerald" },
    { l: "Settlement forecast (wk)", v: fmt(x.settlementDue*1.08), tone: "sky" },
    { l: "Risk forecast (30d)", v: `${Math.max(0, x.risk-4)}/100`, tone: x.risk<50?"emerald":"amber" },
    { l: "Expansion potential", v: fmt(x.revenue*0.6), tone: "emerald" },
    { l: "Parent sentiment", v: `${(x.parentRating*20).toFixed(0)}%`, tone: x.parentRating>4?"emerald":"amber" },
    { l: "School sentiment", v: `${(x.schoolRating*20).toFixed(0)}%`, tone: x.schoolRating>4?"emerald":"amber" },
    { l: "Compliance horizon", v: x.kycComplete?"Stable 90d":"Action needed 30d", tone: x.kycComplete?"emerald":"amber" },
  ];
  const tones: any = { emerald: "border-emerald-500/40 text-emerald-700 dark:text-emerald-400", amber: "border-amber-500/40 text-amber-700 dark:text-amber-400", sky: "border-sky-500/40 text-sky-700 dark:text-sky-400" };
  return (
    <div className="space-y-3">
      <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-4">
        <div className="flex items-center gap-2 font-display text-sm"><Sparkles className="h-4 w-4 text-primary"/>Merchant Digital Twin</div>
        <p className="mt-1 text-xs text-muted-foreground">Live simulation of this merchant&apos;s economic, operational and reputational trajectory. Updated every minute from POS, wallet, settlement and sentiment streams.</p>
      </Card>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {tw.map(t=>(
          <Card key={t.l} className={`p-3 ${tones[t.tone]}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-80">{t.l}</div>
            <div className="mt-1 font-display text-lg">{t.v}</div>
          </Card>
        ))}
      </div>
      <Card className="p-4">
        <div className="mb-2 flex items-center gap-2 font-display text-sm"><Activity className="h-4 w-4"/>Decision recommendation</div>
        <ul className="space-y-1.5 text-sm">
          {[
            x.risk>60 && "Hold next settlement until KYC refresh is complete.",
            x.refunds>0.04 && "Investigate refund spike — possible quality issue.",
            x.schoolsServed<5 && "Recommend expansion to 3 additional schools.",
            x.parentRating<4 && "Trigger parent satisfaction survey.",
            x.deliveryRel<85 && "Audit delivery operations — reliability below target.",
            "Increase settlement frequency to T+1 (top 20% performer).",
          ].filter(Boolean).map((r,i)=>(
            <li key={i} className="flex items-start gap-2"><Zap className="mt-0.5 h-3.5 w-3.5 text-amber-500"/>{r}</li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
